import { useCallback, useEffect, useMemo, useState } from 'react';

import { useAppSelector, useAppDispatch } from 'redux/store';
import type { CreateTarget } from 'actions';
import { useTranslation } from 'react-i18next';
import { notify } from 'messages';
import Button from 'components/base/Button';
import Modal from 'react-bootstrap/Modal';
import Col from 'react-bootstrap/Col';
import Row from 'react-bootstrap/Row';
import Form from 'react-bootstrap/Form';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faSave, faWindowClose } from '@fortawesome/free-solid-svg-icons';
import { Dropdown } from 'react-bootstrap';
import Select from 'react-select';
import { updateTrigger, createTrigger } from '../../../../redux/slices/triggersSlice';
import {
  TRIGGER_ACTIONS,
  TRIGGER_RESTART_FIRE_MODES,
  type TriggerAction,
  type TriggerParameter,
  type TriggerRestartFireMode,
  type TriggerStatusList,
} from 'api/triggers/endpoints/types';
import type { SaveTrigger } from 'api/triggers/endpoints/postTrigger';
import type { Trigger } from 'api/triggers/endpoints/getTrigger';
import { getZones } from '../../../../redux/slices/lists/zonesSlice';
import { FolderSelect } from 'components/forms/FolderSelect';
import { fetchCustomerNodeDefinitionById } from '../../../../redux/slices/customer_node_definitionsSlice';
import { getQueues } from '../../../../redux/slices/lists/queuesSlice';
import { EditNodeDefinitionReference, type NodeDefinitionReference } from 'edit/reference/EditNodeDefinitionReference';
import { EditEventReference } from 'edit/reference/EditEventReference';
import { EditTimeZoneReference } from 'edit/reference/EditTimeZoneReference';
import { statusOptions } from 'utils/constants';
import { useUnsavedChanges } from '../../../../hooks/useUnsavedChanges';
import ConfirmationModal from '../../../../components/common/ConfirmationModal';
import { getAuthorizedZones, type AuthorizedZone } from 'api/zones/endpoints/getAuthorizedZones';
import { acceptDegraded } from 'validation';
import { RESTART_FIRE_MODE_LABELS, TRIGGER_ACTION_LABELS } from './triggerLabels';

interface NodeDefinitionParameter {
  name: string;
  parameter_type: string;
  default_expression?: string;
  mandatory?: boolean;
  description?: string;
  inout?: string;
}

const NODE_STATUS_VALUES = statusOptions;

interface StatusOption {
  value: string;
  label: string;
}

const STATUS_SELECT_OPTIONS: StatusOption[] = NODE_STATUS_VALUES.map((status) => ({ value: status, label: status }));

/**
 * react-select values for the statuses the form holds, in selection order.
 * A status outside the canonical list is still offered, so loading a trigger
 * cannot drop it before the user saves.
 *
 * @param statuses - Currently selected statuses.
 * @returns Options in the same order.
 */
const statusSelectValue = (statuses: readonly string[]): StatusOption[] =>
  statuses.map((status) => STATUS_SELECT_OPTIONS.find((option) => option.value === status) ?? { value: status, label: status });

/**
 * Empty selection is any status, stored as null. A non-empty selection is the
 * list. The API rejects an empty array.
 *
 * @param statuses - Currently selected statuses.
 * @returns The wire value.
 */
const statusListForSave = (statuses: readonly string[]): TriggerStatusList => (statuses.length === 0 ? null : [...statuses]);

const EditTrigger = (props: {
  // The record to edit, already loaded and boundary-validated by showEditTrigger.
  // Absent for a create. The dialog never reads it from the list slice: the list
  // holds FLAT rows and this is the NESTED shape, which is why every field below
  // reads `status_change` directly and no longer falls back to a flat twin.
  trigger?: Trigger;
  newTrigger?: boolean;
  asDropdownItem?: boolean;
  forceShow?: boolean;
  onRequestClose?: () => void;
  hideTrigger?: boolean;
  // Workspace create pre-target: seeds the zone + folder of a new trigger.
  createIn?: CreateTarget;
}) => {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();

  const [show, setShow] = useState(false);
  const [name, setName] = useState('');
  const [zoneId, setZoneId] = useState<number | null>(null);
  const [folderId, setFolderId] = useState<number | null>(null);
  const [type, setType] = useState('node_status_change');
  const [nodeDefinitionId, setNodeDefinitionId] = useState<number | null>(null);
  const [fromStatus, setFromStatus] = useState<readonly string[]>([]);
  const [toStatus, setToStatus] = useState<readonly string[]>([]);
  const [restartFireMode, setRestartFireMode] = useState<TriggerRestartFireMode>('all');
  const [action, setAction] = useState<TriggerAction>('raise_event');
  const [targetNodeDefinitionId, setTargetNodeDefinitionId] = useState<number | null>(null);
  const [targetEventId, setTargetEventId] = useState<number | null>(null);
  const [queueId, setQueueId] = useState<number | null>(null);
  const [timezone, setTimezone] = useState<string | null>(null);
  const [parameters, setParameters] = useState<TriggerParameter[]>([]);
  const [targetNodeParameters, setTargetNodeParameters] = useState<NodeDefinitionParameter[]>([]);
  const [monitorZoneIds, setMonitorZoneIds] = useState<number[]>([]);
  const [monitorAllZones, setMonitorAllZones] = useState(false);
  const [availableMonitorZones, setAvailableMonitorZones] = useState<AuthorizedZone[]>([]);
  const [showCloseConfirm, setShowCloseConfirm] = useState(false);

  const currentFormValues = useMemo(
    () => ({
      name,
      zoneId,
      folderId,
      type,
      nodeDefinitionId,
      fromStatus,
      toStatus,
      restartFireMode,
      action,
      targetNodeDefinitionId,
      targetEventId,
      queueId,
      timezone,
      parameters,
      monitorZoneIds,
      monitorAllZones,
    }),
    [
      name,
      zoneId,
      folderId,
      type,
      nodeDefinitionId,
      fromStatus,
      toStatus,
      restartFireMode,
      action,
      targetNodeDefinitionId,
      targetEventId,
      queueId,
      timezone,
      parameters,
      monitorZoneIds,
      monitorAllZones,
    ]
  );
  const isDirtyRaw = useUnsavedChanges(show, currentFormValues);
  const isDirty = !props.newTrigger && isDirtyRaw;

  const trigger_id = props.newTrigger ? undefined : props.trigger?.trigger_id;

  const fetchedTrigger = props.newTrigger ? null : props.trigger;

  const zones = useAppSelector((state) => state.listOfZones?.zones || []);

  const queues = useAppSelector((state) => state.queues?.queues || []);

  useEffect(() => {
    dispatch(getZones() as any);
    dispatch(getQueues() as any);
    const zoneAction = props.newTrigger ? 'create' : 'update';
    // NOTE: `acceptDegraded` here -- the chain ends in an empty catch, so
    // refusing a degraded answer would empty the zone picker with no
    // explanation. This is what the endpoint did before it stopped collapsing
    // internally.
    getAuthorizedZones('triggers', zoneAction)()
      .then(acceptDegraded)
      .then((zones) => setAvailableMonitorZones([...zones]))
      .catch(() => {});
  }, [dispatch, props.newTrigger]);

  useEffect(() => {
    if (props.forceShow !== undefined) {
      setShow(props.forceShow);
    }
  }, [props.forceShow]);

  useEffect(() => {
    if (fetchedTrigger && !props.newTrigger) {
      setName(fetchedTrigger.name || '');
      setZoneId(fetchedTrigger.zone_id || null);
      setFolderId(fetchedTrigger.folder_id ?? null);
      setType(fetchedTrigger.type || 'node_status_change');
      setNodeDefinitionId(fetchedTrigger.status_change?.node_definition_id ?? null);
      setFromStatus([...(fetchedTrigger.status_change?.from_status ?? [])]);
      setToStatus([...(fetchedTrigger.status_change?.to_status ?? [])]);
      setRestartFireMode(fetchedTrigger.status_change?.restart_fire_mode ?? 'all');
      setAction(fetchedTrigger.status_change?.action ?? 'raise_event');
      setTargetNodeDefinitionId(fetchedTrigger.status_change?.target_node_definition_id ?? null);
      setTargetEventId(fetchedTrigger.status_change?.target_event_id ?? null);
      setQueueId(fetchedTrigger.status_change?.queue_id ?? null);
      setTimezone(fetchedTrigger.status_change?.timezone ?? null);
      setParameters([...(fetchedTrigger.status_change?.parameters ?? [])]);
      setMonitorAllZones(fetchedTrigger.monitor_all_zones || false);
      setMonitorZoneIds([...fetchedTrigger.monitor_zone_ids]);
    }
  }, [fetchedTrigger, props.newTrigger]);

  // Seed a new trigger's zone/folder from the workspace create pre-target.
  useEffect(() => {
    if (props.newTrigger && props.createIn) {
      setZoneId(props.createIn.zoneId);
      setFolderId(props.createIn.folderId);
    }
  }, [props.newTrigger, props.createIn]);

  // Auto-seed monitor zones with the defining zone for new triggers.
  useEffect(() => {
    if (props.newTrigger && zoneId !== null && !monitorAllZones) {
      setMonitorZoneIds((prev) => (prev.length === 0 ? [zoneId] : prev));
    }
  }, [zoneId, props.newTrigger, monitorAllZones]);

  // Fetch target node definition parameters when target changes
  useEffect(() => {
    if (targetNodeDefinitionId && action === 'submit_child') {
      dispatch(fetchCustomerNodeDefinitionById(targetNodeDefinitionId) as any)
        .then((result: any) => {
          if (result.payload?.parameters) {
            const nodeParams: NodeDefinitionParameter[] = result.payload.parameters;
            setTargetNodeParameters(nodeParams);
            // Merge: preserve any saved values, initialize new params to empty.
            // Uses functional update to read current state instead of stale closure.
            setParameters((prev) =>
              nodeParams
                .filter((p) => p.inout !== 'out')
                .map((p) => {
                  const saved = prev.find((sp) => sp.name === p.name);
                  return {
                    name: p.name,
                    value: saved?.value ?? p.default_expression ?? '',
                  };
                })
            );
          } else {
            setTargetNodeParameters([]);
          }
        })
        .catch(() => {
          setTargetNodeParameters([]);
        });
    } else {
      setTargetNodeParameters([]);
    }
  }, [targetNodeDefinitionId, action, dispatch]);

  const handleClose = () => {
    setShow(false);
    if (props.newTrigger) {
      setName('');
      setZoneId(null);
      setFolderId(null);
      setType('node_status_change');
      setNodeDefinitionId(null);
      setFromStatus([]);
      setToStatus([]);
      setRestartFireMode('all');
      setAction('raise_event');
      setTargetNodeDefinitionId(null);
      setTargetEventId(null);
      setQueueId(null);
      setTimezone(null);
      setParameters([]);
      setTargetNodeParameters([]);
      setMonitorZoneIds([]);
      setMonitorAllZones(false);
    }
    if (props.onRequestClose) props.onRequestClose();
  };

  const handleRequestClose = () => {
    if (isDirty) {
      setShowCloseConfirm(true);
    } else {
      handleClose();
    }
  };

  const handleShow = () => {
    setShow(true);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!name.trim()) {
      notify.error('Name is required');
      return;
    }

    if (!zoneId) {
      notify.error('Zone is required');
      return;
    }

    if (action === 'submit_child' && !targetNodeDefinitionId) {
      notify.error('Target Node Definition is required when action is Submit Child Node');
      return;
    }

    if (action === 'raise_event' && !targetEventId) {
      notify.error('Target Event is required when action is Raise Event');
      return;
    }

    if (!monitorAllZones && monitorZoneIds.length === 0) {
      notify.error('Select at least one Monitor Zone, or enable "Monitor all zones"');
      return;
    }

    // Typed as SaveTrigger, so every wire field must be MENTIONED here even when
    // its value is null — the update route branches on hasOwnProperty for
    // folder_id and monitor_zone_ids, where an absent key and an explicit value
    // mean opposite things (an empty monitor list is "all zones", not "none").
    const save: SaveTrigger = {
      name: name.trim(),
      zone_id: zoneId,
      folder_id: folderId,
      type,
      monitor_zone_ids: monitorAllZones ? [] : monitorZoneIds,
      node_definition_id: nodeDefinitionId || null,
      from_status: statusListForSave(fromStatus),
      to_status: statusListForSave(toStatus),
      restart_fire_mode: restartFireMode,
      action,
      target_node_definition_id: action === 'submit_child' ? targetNodeDefinitionId : null,
      target_event_id: action === 'raise_event' ? targetEventId : null,
      queue_id: action === 'submit_child' ? queueId : null,
      parameters: action === 'submit_child' ? parameters : [],
      timezone: action === 'submit_child' ? timezone : null,
    };

    // The slice owns every message a write produces — success, the unreadable
    // receipt (#1035) and the failure — so there is nothing to report here beyond
    // whether to close. A rejected write did not happen, so the form stays open
    // with the user's input intact.
    if (props.newTrigger) {
      const created = await dispatch(createTrigger(save));
      if (created.meta.requestStatus === 'rejected') return;
      handleClose();
      return;
    }

    // Fails CLOSED rather than falling through to create. Unreachable today —
    // showEditTrigger does not open the dialog without a record — but the
    // alternative shape, deciding create-vs-update on whether a record happens to
    // be loaded, is how an "Edit" dialog silently POSTs a duplicate (#1035).
    if (trigger_id === undefined) {
      notify.error('This trigger was not loaded, so it cannot be saved. Please close the dialog and try again.');
      return;
    }

    const updated = await dispatch(updateTrigger({ id: trigger_id, save }));
    if (updated.meta.requestStatus === 'rejected') return;

    handleClose();
  };

  // Node definitions are scoped server-side to the selected zone's name
  // (EditNodeDefinitionReference's `zoneName` → backend `zone_name`).
  const selectedZone = zones.find((z: any) => z.zone_id === zoneId);

  const monitorZoneOptions = availableMonitorZones.map((z) => ({
    value: z.zone_id,
    label: z.name,
  }));
  const monitorZoneValue = monitorZoneOptions.filter((o) => monitorZoneIds.includes(o.value));
  const isBroadFilter = monitorAllZones && !nodeDefinitionId && fromStatus.length === 0 && toStatus.length === 0;

  const [watchNode, setWatchNode] = useNodeDefinitionReference(nodeDefinitionId, setNodeDefinitionId);
  const [targetNode, setTargetNode] = useNodeDefinitionReference(targetNodeDefinitionId, setTargetNodeDefinitionId);

  // Filter queues by selected zone
  const filteredQueues = zoneId && selectedZone ? queues.filter((q: any) => q.zone_name === selectedZone.name) : queues;

  // Helper to update a parameter value
  const updateParameterValue = (paramName: string, value: string) => {
    setParameters((prev) => {
      const existing = prev.find((p) => p.name === paramName);
      if (existing) {
        return prev.map((p) => (p.name === paramName ? { ...p, value } : p));
      }
      return [...prev, { name: paramName, value }];
    });
  };

  // The text a parameter's box shows. A value is any JSON value, so a non-string is
  // shown as JSON; it is only replaced — by the typed string — if the user edits the
  // box, and an untouched one is saved back as it arrived.
  const getParameterValue = (paramName: string): string => {
    const value = parameters.find((p) => p.name === paramName)?.value;
    if (value === undefined || value === null) return '';
    // `?? ''` because the lib types lie: JSON.stringify is declared `string` but
    // returns undefined for a function or a symbol.
    return typeof value === 'string' ? value : (JSON.stringify(value) ?? '');
  };

  return (
    <>
      {!props.hideTrigger ? (
        <>
          {props.asDropdownItem ? (
            <Dropdown.Item onClick={handleShow}>{props.newTrigger ? t('button.new') : t('button.edit')}</Dropdown.Item>
          ) : (
            <Button variant={props.newTrigger ? 'primary' : 'phoenix-secondary'} onClick={handleShow}>
              {props.newTrigger ? t('button.new') : t('button.edit')}
            </Button>
          )}
        </>
      ) : null}

      <Modal show={show} onHide={handleRequestClose} backdrop="static" keyboard={false} size="lg">
        <Modal.Header>
          <Modal.Title>{props.newTrigger ? t('button.newe') : t('button.edit')} Trigger</Modal.Title>
        </Modal.Header>

        <Modal.Body>
          <Form.Group as={Row} className="mb-3" controlId="formTriggerName">
            <Form.Label column sm="3">
              {t('name')} *
            </Form.Label>
            <Col sm="9">
              <Form.Control type="text" value={name} onChange={(e) => setName(e.target.value)} placeholder="Enter trigger name" />
            </Col>
          </Form.Group>

          <Form.Group as={Row} className="mb-3" controlId="formTriggerZone">
            <Form.Label column sm="3">
              Zone *
            </Form.Label>
            <Col sm="9">
              <Form.Select
                value={zoneId ?? ''}
                onChange={(e) => {
                  const newZoneId = e.target.value ? parseInt(e.target.value, 10) : null;
                  setZoneId(newZoneId);
                  // Reset node definitions, events, queue, parameters, and the
                  // (zone-scoped) folder when zone changes
                  setNodeDefinitionId(null);
                  setTargetNodeDefinitionId(null);
                  setTargetEventId(null);
                  setQueueId(null);
                  setParameters([]);
                  setTargetNodeParameters([]);
                  setFolderId(null);
                }}
              >
                <option value="">Select a zone</option>
                {zones.map((zone: any) => (
                  <option key={zone.zone_id} value={zone.zone_id}>
                    {zone.name}
                  </option>
                ))}
              </Form.Select>
            </Col>
          </Form.Group>

          <Form.Group as={Row} className="mb-3" controlId="formTriggerFolder">
            <Form.Label column sm="3">
              Folder
            </Form.Label>
            <Col sm="9">
              <FolderSelect value={folderId} onChange={setFolderId} zoneId={zoneId} />
            </Col>
          </Form.Group>

          <Form.Group as={Row} className="mb-3" controlId="formTriggerMonitorAllZones">
            <Form.Label column sm="3">
              Monitor All Zones
            </Form.Label>
            <Col sm="9" className="d-flex align-items-center">
              <Form.Check
                type="checkbox"
                id="monitorAllZonesCheck"
                checked={monitorAllZones}
                onChange={(e) => {
                  setMonitorAllZones(e.target.checked);
                  if (e.target.checked) {
                    setMonitorZoneIds([]);
                  } else {
                    // Seed with the defining zone so the payload is never
                    // accidentally empty (empty = all-zones on the backend).
                    setMonitorZoneIds(zoneId !== null ? [zoneId] : []);
                  }
                }}
                label="Monitor status changes across all zones"
              />
            </Col>
          </Form.Group>

          {!monitorAllZones && (
            <Form.Group as={Row} className="mb-3" controlId="formTriggerMonitorZones">
              <Form.Label column sm="3">
                Monitor Zones
              </Form.Label>
              <Col sm="9">
                <Select
                  isMulti
                  options={monitorZoneOptions}
                  value={monitorZoneValue}
                  onChange={(selected) => setMonitorZoneIds((selected as typeof monitorZoneOptions).map((o) => o.value))}
                  placeholder="Select zones to monitor..."
                  classNamePrefix="react-select"
                />
                <Form.Text className="text-muted">Zones whose node status changes will fire this trigger. Defaults to the defining zone.</Form.Text>
              </Col>
            </Form.Group>
          )}

          {isBroadFilter && (
            <Row className="mb-3">
              <Col sm={{ span: 9, offset: 3 }}>
                <div className="alert alert-danger py-2 mb-0">
                  <strong>Broad filter:</strong> This trigger monitors all zones with no node, from-status, or to-status filter. It will fire on every node
                  status change system-wide.
                </div>
              </Col>
            </Row>
          )}

          <Form.Group as={Row} className="mb-3" controlId="formTriggerType">
            <Form.Label column sm="3">
              Type
            </Form.Label>
            <Col sm="9">
              <Form.Select value={type} onChange={(e) => setType(e.target.value)}>
                <option value="node_status_change">Node Status Change</option>
              </Form.Select>
            </Col>
          </Form.Group>

          {type === 'node_status_change' && (
            <>
              <Form.Group as={Row} className="mb-3" controlId="formTriggerNodeDefinition">
                <Form.Label column sm="3">
                  Watch Node
                </Form.Label>
                <Col sm="9">
                  <EditNodeDefinitionReference value={watchNode} onChange={setWatchNode} zoneName={selectedZone?.name} />
                  <Form.Text className="text-muted">Select which node to watch for status changes, or leave empty to watch all nodes in the zone</Form.Text>
                </Col>
              </Form.Group>

              <Form.Group as={Row} className="mb-3" controlId="formTriggerFromStatus">
                <Form.Label column sm="3">
                  From Status
                </Form.Label>
                <Col sm="9">
                  <Select
                    isMulti
                    options={STATUS_SELECT_OPTIONS}
                    value={statusSelectValue(fromStatus)}
                    onChange={(selected) => setFromStatus(((selected ?? []) as StatusOption[]).map((option) => option.value))}
                    placeholder="Any status"
                    closeMenuOnSelect={false}
                    classNamePrefix="react-select"
                  />
                  <Form.Text className="text-muted">Leave empty to trigger on any source status</Form.Text>
                </Col>
              </Form.Group>

              <Form.Group as={Row} className="mb-3" controlId="formTriggerToStatus">
                <Form.Label column sm="3">
                  To Status
                </Form.Label>
                <Col sm="9">
                  <Select
                    isMulti
                    options={STATUS_SELECT_OPTIONS}
                    value={statusSelectValue(toStatus)}
                    onChange={(selected) => setToStatus(((selected ?? []) as StatusOption[]).map((option) => option.value))}
                    placeholder="Any status"
                    closeMenuOnSelect={false}
                    classNamePrefix="react-select"
                  />
                  <Form.Text className="text-muted">
                    Leave empty to trigger on any target status. The trigger fires for every combination of a selected source status and a selected target
                    status.
                  </Form.Text>
                </Col>
              </Form.Group>

              {(toStatus.length === 0 || toStatus.includes('failed')) && (
                <Form.Group as={Row} className="mb-3" controlId="formTriggerRestartFireMode">
                  <Form.Label column sm="3">
                    Auto-restart firing
                  </Form.Label>
                  <Col sm="9">
                    <Form.Select value={restartFireMode} onChange={(e) => setRestartFireMode(e.target.value as TriggerRestartFireMode)}>
                      {TRIGGER_RESTART_FIRE_MODES.map((mode) => (
                        <option key={mode} value={mode}>
                          {RESTART_FIRE_MODE_LABELS[mode]}
                        </option>
                      ))}
                    </Form.Select>
                    <Form.Text className="text-muted">
                      When the watched node has auto-restart enabled, controls which failure in the retry cycle fires this trigger.
                    </Form.Text>
                  </Col>
                </Form.Group>
              )}

              <Form.Group as={Row} className="mb-3" controlId="formTriggerAction">
                <Form.Label column sm="3">
                  Action *
                </Form.Label>
                <Col sm="9">
                  <Form.Select
                    value={action}
                    onChange={(e) => {
                      const next = e.target.value as TriggerAction;
                      setAction(next);
                      if (next !== 'submit_child') {
                        setTargetNodeDefinitionId(null);
                        setQueueId(null);
                        setTimezone(null);
                        setParameters([]);
                        setTargetNodeParameters([]);
                      }
                      if (next !== 'raise_event') {
                        setTargetEventId(null);
                      }
                    }}
                  >
                    {TRIGGER_ACTIONS.map((act) => (
                      <option key={act} value={act}>
                        {TRIGGER_ACTION_LABELS[act]}
                      </option>
                    ))}
                  </Form.Select>
                  <Form.Text className="text-muted">Action to perform when the trigger condition is met</Form.Text>
                </Col>
              </Form.Group>

              {action === 'submit_child' && (
                <>
                  <Form.Group as={Row} className="mb-3" controlId="formTriggerTargetNodeDefinition">
                    <Form.Label column sm="3">
                      Target Node *
                    </Form.Label>
                    <Col sm="9">
                      <EditNodeDefinitionReference
                        mandatory
                        value={targetNode}
                        onChange={(nd) => {
                          setTargetNode(nd);
                          // Reset parameters when target node changes
                          setParameters([]);
                          setTargetNodeParameters([]);
                        }}
                        zoneName={selectedZone?.name}
                      />
                      <Form.Text className="text-muted">The node definition to submit when the trigger fires</Form.Text>
                    </Col>
                  </Form.Group>

                  {targetNodeDefinitionId && (
                    <>
                      <Form.Group as={Row} className="mb-3" controlId="formTriggerQueue">
                        <Form.Label column sm="3">
                          Queue
                        </Form.Label>
                        <Col sm="9">
                          <Form.Select value={queueId ?? ''} onChange={(e) => setQueueId(e.target.value ? parseInt(e.target.value, 10) : null)}>
                            <option value="">Use triggering node&apos;s queue (or target&apos;s default)</option>
                            {filteredQueues.map((q: any) => (
                              <option key={q.queue_id} value={q.queue_id}>
                                {q.queue_name}
                              </option>
                            ))}
                          </Form.Select>
                          <Form.Text className="text-muted">
                            Select queue for the submitted node, or leave empty to use the triggering node&apos;s queue
                          </Form.Text>
                        </Col>
                      </Form.Group>

                      <Form.Group as={Row} className="mb-3" controlId="formTriggerTimezone">
                        <Form.Label column sm="3">
                          Submit Timezone
                        </Form.Label>
                        <Col sm="9">
                          <EditTimeZoneReference value={timezone} onChange={setTimezone} placeholder="use the configured default timezone" />
                        </Col>
                      </Form.Group>

                      {targetNodeParameters.length > 0 && (
                        <Form.Group as={Row} className="mb-3">
                          <Form.Label column sm="3">
                            Parameters
                          </Form.Label>
                          <Col sm="9">
                            <div className="border rounded p-3 bg-body-tertiary">
                              <Form.Text className="text-muted d-block mb-3">
                                Set parameter values. You can use template variables:
                                <code className="ms-1">{'{{trigger_node_instance_id}}'}</code>,<code className="ms-1">{'{{trigger_node_definition_id}}'}</code>,
                                <code className="ms-1">{'{{trigger_node_definition_name}}'}</code>,<code className="ms-1">{'{{trigger_status_from}}'}</code>,
                                <code className="ms-1">{'{{trigger_status_to}}'}</code>
                              </Form.Text>
                              {targetNodeParameters
                                .filter((p) => p.inout !== 'out')
                                .map((param) => (
                                  <Form.Group key={param.name} className="mb-2" controlId={`param-${param.name}`}>
                                    <Form.Label className="mb-1">
                                      {param.name}
                                      {param.mandatory && <span className="text-danger">*</span>}
                                      <small className="text-muted ms-2">({param.parameter_type})</small>
                                    </Form.Label>
                                    <Form.Control
                                      type="text"
                                      value={getParameterValue(param.name)}
                                      onChange={(e) => updateParameterValue(param.name, e.target.value)}
                                      placeholder={param.default_expression || `Enter ${param.name}`}
                                    />
                                    {param.description && <Form.Text className="text-muted">{param.description}</Form.Text>}
                                  </Form.Group>
                                ))}
                            </div>
                          </Col>
                        </Form.Group>
                      )}
                    </>
                  )}
                </>
              )}

              {action === 'raise_event' && (
                <Form.Group as={Row} className="mb-3" controlId="formTriggerTargetEvent">
                  <Form.Label column sm="3">
                    Target Event *
                  </Form.Label>
                  <Col sm="9">
                    {/* Every kind, not just process events: a trigger raises by id, and this picker has
                        always offered file events too. */}
                    <EditEventReference
                      placeholder="Select target event"
                      value={targetEventId}
                      onChange={setTargetEventId}
                      zoneName={selectedZone?.name}
                      type="all"
                    />
                    <Form.Text className="text-muted">The event to raise when the trigger fires</Form.Text>
                  </Col>
                </Form.Group>
              )}
            </>
          )}
        </Modal.Body>

        <Modal.Footer>
          <Button variant="phoenix-danger" onClick={handleRequestClose} startIcon={<FontAwesomeIcon icon={faWindowClose} />}>
            {t('button.close')}
          </Button>
          <Button variant="phoenix-primary" onClick={handleSubmit} startIcon={<FontAwesomeIcon icon={faSave} />}>
            {props.newTrigger ? t('button.create') : t('button.saveAndClose')}
          </Button>
        </Modal.Footer>
        <ConfirmationModal
          show={showCloseConfirm}
          title="Unsaved Changes"
          body="You have unsaved changes. Are you sure you want to close without saving?"
          confirmText="Discard Changes"
          confirmVariant="danger"
          onConfirm={() => {
            setShowCloseConfirm(false);
            handleClose();
          }}
          onCancel={() => setShowCloseConfirm(false)}
        />
      </Modal>
    </>
  );
};

export default EditTrigger;

// Bridge between this dialog's id-based state and
// EditNodeDefinitionReference's object-based prop API. Inflates an
// id into a partial T so the wrapper's getKey lookup finds the real
// loaded item once the LOV resolves; deflates the picked T back to
// its id for the parent's setter. The placeholder strings are only
// visible in the brief moment before the LOV loads (and only when
// editing an existing trigger). Temporary scaffolding — when this
// dialog migrates to the new MobX edit infrastructure, the id-based
// state goes away and so does this hook.
const useNodeDefinitionReference = (
  id: number | null,
  setId: (id: number | null) => void
): [NodeDefinitionReference | null, (value: NodeDefinitionReference | null) => void] => {
  const value = useMemo<NodeDefinitionReference | null>(() => (id !== null ? { node_definition_id: id, name: '', zone_name: '' } : null), [id]);
  const onChange = useCallback((nd: NodeDefinitionReference | null) => setId(nd?.node_definition_id ?? null), [setId]);
  return [value, onChange];
};
